import BlogEmailCapture from '@/components/_blog-updates/EmailCapture';
import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import Image from 'next/image';
import { FC } from 'react';

type Theme = 'citron' | 'light' | 'noContainer' | 'imageThumbnail';

const defaultTheme: Theme = 'citron';

export type CtaSidebarProps = {
  theme?: Theme;
  showAvatars?: boolean;
  hasEmailCapture?: boolean;
  textLink?: boolean;
  image?: string;
  title: string;
  description?: string;
  textLinkText?: string;
  textLinkHref?: string;
  primaryButton?: ButtonWithLinkOrAction;
  secondaryButton?: ButtonWithLinkOrAction;
  avatarsData?: Array<{ src: string; alt?: string }>;
}

const CtaSidebar: FC<CtaSidebarProps> = ({
  theme,
  showAvatars = false,
  hasEmailCapture = false,
  textLink = false,
  image,
  title,
  description,
  textLinkText,
  textLinkHref,
  primaryButton,
  secondaryButton,
  avatarsData,
}) => {
  const currentTheme = theme || defaultTheme;
  const { modalActionHandler } = useModalButtonActions();

  const emailCaptureTheme = {
    citron: 'blue',
    light: 'citron',
    noContainer: 'gray',
  }[currentTheme] as 'blue' | 'citron' | 'gray';

  const OuterEl = textLink ? 'a' : 'div';

  return (
    <OuterEl
      className={cn(
        'text-blue-900 flex flex-col md:items-center gap-4 overflow-hidden group max-md:hidden',
        {
          'bg-citron-500 p-4 rounded-12': currentTheme === 'citron',
          'bg-gray-100 p-4 rounded-12': currentTheme === 'light' || currentTheme === 'imageThumbnail',
        }
      )}
      {...(textLink ? { href: textLinkHref } : {})}
    >
      <div>
        {showAvatars && (
          <div className="flex items-center mb-3">
            {avatarsData?.map((avatar, index) => (
              <Image
                key={index}
                src={avatar.src}
                width={40}
                height={40}
                alt={avatar.alt || `Avatar ${index + 1}`}
                className={cn(
                  'border-solid border-[3px] w-[54px] h-[54px] rounded-circle transition-transform duration-800 ease-appear',
                  {
                    'mr-[-15px]': index > 0,
                    'border-citron-500': currentTheme === 'citron',
                    'border-gray-100': currentTheme === 'light' || currentTheme === 'imageThumbnail',
                    'border-white': currentTheme === 'noContainer',
                    'group-hover:translate-x-[15px]': index === 1,
                    'group-hover:translate-x-[30px]': index === 2,
                  }
                )}
              />
            ))}
          </div>
        )}
        {image && (
          <div className='rounded-[8px] overflow-hidden mx-[-24px] mt-[-24px] mb-4 bg-gray-200 aspect-video relative'>
            <Image
              src={image}
              alt=""
              fill
              className='object-cover transition-transform duration-800 ease-appear group-hover:scale-[1.1]'
            />
          </div>
        )}
        <h5 className="text-h3 font-500 mt-[2px] max-w-[16em] text-pretty">
          {title.split(' ').map((word, index, array) =>
            index === array.length - 1 ? <span key={index} className='font-serif'>{word}</span> : <span key={index}>{word} </span>
          )}
        </h5>
        {description && (
          <p className="text-caption1 mt-[12px] text-gray-800">
            {description}
          </p>
        )}
      </div>

      <div className={cn(
        'flex gap-1 w-full',
        {
          'mt-2': showAvatars === false && currentTheme !== 'imageThumbnail',
          'mt-1': currentTheme === 'imageThumbnail',
        }
      )}>
        {hasEmailCapture && (
          <BlogEmailCapture theme={emailCaptureTheme} />
        )}
        {textLink && (
          <span className='inline-flex items-center gap-1 text-blue-90 text-p2 font-500'>
            {textLinkText}
            <span className="inline-block overflow-hidden relative">
              <span className="opacity-0">→</span>
              <span className='absolute left-0 top-0 inline-block transition-transform duration-800 ease-appear group-hover:translate-x-[101%]'>→</span>
              <span className='absolute left-0 top-0 inline-block transition-transform duration-800 ease-appear translate-x-[-101%] group-hover:translate-x-[0]'>→</span>
            </span>
          </span>
        )}
        {primaryButton && (
          primaryButton.href ? (
            <a
              href={primaryButton.href}
              className={cn(
                'button button-size-medium-blog max-md:w-full',
                {
                  'button-tertiary-blue-900': currentTheme === 'citron' || currentTheme === 'noContainer',
                  'button-primary-citron': currentTheme === 'light',
                }
              )}
            >
              {primaryButton.title}
            </a>
          ) : (
            <button
              onClick={() => modalActionHandler(primaryButton.action)}
              className={cn(
                'button button-size-medium-blog max-md:w-full',
                {
                  'button-tertiary-blue-900': currentTheme === 'citron' || currentTheme === 'noContainer',
                  'button-primary-citron': currentTheme === 'light',
                }
              )}
            >
              {primaryButton.title}
            </button>
          )
        )}
        {secondaryButton && (
          secondaryButton.href ? (
            <a href={secondaryButton.href} className={`button button-size-medium-blog button-secondary-blue-900 max-md:w-full`}>
              {secondaryButton.title}
            </a>
          ) : (
            <button onClick={() => modalActionHandler(secondaryButton.action)} className={`button button-size-medium-blog button-secondary-blue-900 max-md:w-full`}>
              {secondaryButton.title}
            </button>
          )
        )}
      </div>
    </OuterEl>
  );
}

export default CtaSidebar;
